import { useChromeThemeColor } from './use-chrome-theme-color'
import { deepEqual } from '@/utils/object-util'

/**
 * Theme properties.
 */
export interface Theme {
  /**
   * Theme brand color.
   */
  brand: string

  /**
   * Theme brand color for dark mode.
   */
  brandDark: string

  /**
   * Theme accent color, usually based on the brand.
   * Used for most things.
   */
  accent: string

  /**
   * Theme accent color for dark mode, usually based on the brand.
   * Used for most things.
   */
  accentDark: string
}

/**
 * The currently effective theme, taking color scheme into account.
 */
export interface EffectiveTheme {
  /**
   * Theme brand color.
   */
  brand: string

  /**
   * Theme accent color, usually based on the brand.
   * Used for most things.
   */
  accent: string

  /**
   * If specified, will override text color variables based on the
   * luminosity of the brand color.
   *
   * For example, if a brand color is too bright, we'll need to change
   * some text labels to be darker instead.
   */
  contrastTextColorOverride?: string
}

export const Theme = {
  /**
   * Checks whether the themes are equal.
   *
   * @param left
   * @param right
   */
  isEqual: (left: Theme | null, right: Theme | null): boolean =>
    deepEqual(left, right),
}

/**
 * Multiple uses of the chrome theme color hook are ordered by layer.
 * Higher number = higher priority.
 */
export const enum ChromeThemeLayer {
  /**
   * Indicates that the theme color is always applicable.
   */
  Root = 0,

  /**
   * Indicates that this represents a top-hugging banner.
   */
  Banner = 1,

  /**
   * Indicates that this represents a modal being open.
   */
  Modal = 2,
}

// Re-export the hook.
export { useChromeThemeColor }
export { type ColorScheme } from './color-scheme'
